<template>
  <div class="admin-page p-4">
    <div class="max-w-5xl mx-auto">
      <!-- Заголовок -->
      <div class="flex align-items-center justify-content-between mb-4">
        <div class="flex align-items-center gap-3">
          <i class="pi pi-cog text-3xl text-primary"></i>
          <div>
            <h1 class="m-0 text-2xl font-bold">Администрирование</h1>
            <p class="m-0 text-surface-500">Управление фоновыми задачами и логами</p>
          </div>
        </div>
        <Button
          label="Кастомные колонки"
          icon="pi pi-table"
          severity="secondary"
          @click="$router.push('/admin/columns')"
        />
      </div>

      <!-- Статус планировщика -->
      <Card class="mb-4">
        <template #content>
          <div class="flex align-items-center justify-content-between">
            <div class="flex align-items-center gap-3">
              <i class="pi pi-server text-2xl text-surface-500"></i>
              <div>
                <div class="font-semibold">Статус планировщика</div>
                <div class="text-sm text-surface-500">APScheduler</div>
              </div>
            </div>
            <div class="flex align-items-center gap-4">
              <Tag 
                :value="schedulerStatus.running ? 'Работает' : 'Остановлен'" 
                :severity="schedulerStatus.running ? 'success' : 'danger'"
                :icon="schedulerStatus.running ? 'pi pi-check-circle' : 'pi pi-times-circle'"
              />
              <div class="text-sm text-surface-500">
                Задач: <strong>{{ schedulerStatus.jobs_count }}</strong>
              </div>
            </div>
          </div>
        </template>
      </Card>

      <!-- Таблица задач -->
      <Card class="mb-4">
        <template #title>
          <div class="flex align-items-center gap-2">
            <i class="pi pi-list"></i>
            <span>Запланированные задачи</span>
          </div>
        </template>
        <template #content>
          <DataTable 
            :value="jobs" 
            :loading="loadingJobs"
            stripedRows
            class="jobs-table"
          >
            <Column field="name" header="Задача" style="min-width: 250px">
              <template #body="{ data }">
                <div class="flex align-items-center gap-2">
                  <i :class="getJobIcon(data.id)" class="text-surface-500"></i>
                  <div>
                    <div class="font-medium">{{ getJobDisplayName(data.id) }}</div>
                    <div class="text-xs text-surface-400">{{ data.id }}</div>
                  </div>
                </div>
              </template>
            </Column>
            
            <Column field="pending" header="Статус" style="width: 130px">
              <template #body="{ data }">
                <Tag 
                  :value="data.pending ? 'На паузе' : 'Активна'" 
                  :severity="data.pending ? 'warning' : 'success'"
                  :icon="data.pending ? 'pi pi-pause' : 'pi pi-play'"
                />
              </template>
            </Column>
            
            <Column field="trigger" header="Расписание" style="width: 180px">
              <template #body="{ data }">
                <div class="text-sm">
                  <div>{{ data.trigger }}</div>
                </div>
              </template>
            </Column>
            
            <Column field="next_run" header="Следующий запуск" style="width: 180px">
              <template #body="{ data }">
                <div class="text-sm">
                  <span v-if="data.next_run">{{ formatDateTime(data.next_run) }}</span>
                  <span v-else class="text-surface-400">—</span>
                </div>
              </template>
            </Column>
            
            <Column header="Действия" style="width: 150px">
              <template #body="{ data }">
                <div class="flex gap-1">
                  <Button
                    icon="pi pi-play"
                    severity="success"
                    variant="text"
                    size="small"
                    v-tooltip.top="'Запустить сейчас'"
                    @click="runJobNow(data.id)"
                    :loading="actionLoading === data.id + '_run'"
                  />
                  <Button
                    v-if="!data.pending"
                    icon="pi pi-pause"
                    severity="warning"
                    variant="text"
                    size="small"
                    v-tooltip.top="'Приостановить'"
                    @click="pauseJob(data.id)"
                    :loading="actionLoading === data.id + '_pause'"
                  />
                  <Button
                    v-else
                    icon="pi pi-play-circle"
                    severity="info"
                    variant="text"
                    size="small"
                    v-tooltip.top="'Возобновить'"
                    @click="resumeJob(data.id)"
                    :loading="actionLoading === data.id + '_resume'"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </template>
      </Card>

      <!-- Статистика логов -->
      <Card>
        <template #title>
          <div class="flex align-items-center gap-2">
            <i class="pi pi-file"></i>
            <span>Логи синхронизации</span>
          </div>
        </template>
        <template #content>
          <div v-if="loadingStats" class="flex justify-content-center p-4">
            <ProgressSpinner style="width: 40px; height: 40px" />
          </div>
          
          <div v-else-if="logsStats">
            <!-- Общая статистика -->
            <div class="grid mb-4">
              <div class="col-12 md:col-4">
                <div class="stat-card p-3 border-round bg-surface-50">
                  <div class="text-sm text-surface-500 mb-1">Всего записей</div>
                  <div class="text-2xl font-bold">{{ logsStats.total.toLocaleString() }}</div>
                </div>
              </div>
              <div class="col-12 md:col-4">
                <div class="stat-card p-3 border-round bg-surface-50">
                  <div class="text-sm text-surface-500 mb-1">Размер данных</div>
                  <div class="text-2xl font-bold">~{{ logsStats.size_estimate_mb.toFixed(1) }} MB</div>
                </div>
              </div>
              <div class="col-12 md:col-4">
                <div class="stat-card p-3 border-round bg-surface-50">
                  <div class="text-sm text-surface-500 mb-1">Хранение</div>
                  <div class="text-2xl font-bold">{{ logsStats.retention_days }} дней</div>
                </div>
              </div>
            </div>

            <!-- Распределение по возрасту -->
            <div class="mb-4">
              <div class="text-sm font-medium mb-2">Распределение по возрасту:</div>
              <div class="grid">
                <div class="col-6 md:col-3">
                  <div class="flex align-items-center gap-2">
                    <div class="w-1rem h-1rem border-round bg-green-500"></div>
                    <span class="text-sm">До 7 дней: {{ (logsStats.by_age?.last_7_days || 0).toLocaleString() }}</span>
                  </div>
                </div>
                <div class="col-6 md:col-3">
                  <div class="flex align-items-center gap-2">
                    <div class="w-1rem h-1rem border-round bg-yellow-500"></div>
                    <span class="text-sm">7-30 дней: {{ (logsStats.by_age?.['7_to_30_days'] || 0).toLocaleString() }}</span>
                  </div>
                </div>
                <div class="col-6 md:col-3">
                  <div class="flex align-items-center gap-2">
                    <div class="w-1rem h-1rem border-round bg-orange-500"></div>
                    <span class="text-sm">30-90 дней: {{ (logsStats.by_age?.['30_to_90_days'] || 0).toLocaleString() }}</span>
                  </div>
                </div>
                <div class="col-6 md:col-3">
                  <div class="flex align-items-center gap-2">
                    <div class="w-1rem h-1rem border-round bg-red-500"></div>
                    <span class="text-sm">Старше 90: {{ (logsStats.by_age?.older_than_90_days || 0).toLocaleString() }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Очистка логов -->
            <div class="flex align-items-center gap-3 p-3 border-round bg-surface-100">
              <div class="flex align-items-center gap-2">
                <label class="text-sm">Удалить записи старше:</label>
                <InputNumber 
                  v-model="cleanupDays" 
                  :min="7" 
                  :max="365"
                  suffix=" дней"
                  class="w-8rem"
                />
              </div>
              <Button
                label="Очистить логи"
                icon="pi pi-trash"
                severity="danger"
                outlined
                @click="confirmCleanup"
                :loading="cleanupLoading"
              />
              <Button
                icon="pi pi-refresh"
                severity="secondary"
                variant="text"
                v-tooltip.top="'Обновить статистику'"
                @click="loadLogsStats"
              />
            </div>
          </div>
        </template>
      </Card>
    </div>

    <!-- Диалог подтверждения очистки -->
    <Dialog 
      v-model:visible="cleanupDialogVisible" 
      header="Подтверждение очистки"
      :modal="true"
      :style="{ width: '450px' }"
    >
      <div class="flex align-items-center gap-3">
        <i class="pi pi-exclamation-triangle text-4xl text-orange-500"></i>
        <div>
          <p class="m-0 mb-2">Вы уверены, что хотите удалить логи старше <strong>{{ cleanupDays }} дней</strong>?</p>
          <p class="m-0 text-sm text-surface-500">Это действие необратимо.</p>
        </div>
      </div>
      <template #footer>
        <Button label="Отмена" severity="secondary" @click="cleanupDialogVisible = false" />
        <Button label="Удалить" severity="danger" icon="pi pi-trash" @click="executeCleanup" :loading="cleanupLoading" />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import Card from 'primevue/card'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import InputNumber from 'primevue/inputnumber'
import Dialog from 'primevue/dialog'
import ProgressSpinner from 'primevue/progressspinner'
import apiService from '@/services/apiService'

const toast = useToast()

// State
const jobs = ref([])
const loadingJobs = ref(false)
const actionLoading = ref(null)
const schedulerStatus = ref({ running: false, jobs_count: 0 })

const logsStats = ref(null)
const loadingStats = ref(false)
const cleanupDays = ref(30)
const cleanupLoading = ref(false)
const cleanupDialogVisible = ref(false)

// Названия задач для отображения
const jobDisplayNames = {
  'partssoft_sync': 'Синхронизация заказов PartsSoft',
  'category_sync': 'Синхронизация категорий',
  'reports_cleanup': 'Очистка старых отчётов',
  'sync_logs_cleanup': 'Очистка логов синхронизации',
  'cache_cleanup': 'Очистка кэша',
  'chat_exports_cleanup': 'Очистка экспортов чата',
  'upload_progress_cleanup': 'Очистка прогресса загрузок',
  'google_sheets_sync': 'Синхронизация Google Sheets',
  'metadata_sync': 'Синхронизация метаданных изображений'
}

const jobIcons = {
  'partssoft_sync': 'pi pi-shopping-cart',
  'category_sync': 'pi pi-sitemap',
  'reports_cleanup': 'pi pi-file-excel',
  'sync_logs_cleanup': 'pi pi-file',
  'cache_cleanup': 'pi pi-database',
  'chat_exports_cleanup': 'pi pi-comments',
  'upload_progress_cleanup': 'pi pi-upload',
  'google_sheets_sync': 'pi pi-table',
  'metadata_sync': 'pi pi-images'
}

// Methods
const getJobDisplayName = (id) => {
  return jobDisplayNames[id] || id
}

const getJobIcon = (id) => {
  return jobIcons[id] || 'pi pi-clock'
}

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const loadJobs = async () => {
  loadingJobs.value = true
  try {
    const [jobsResponse, statusResponse] = await Promise.all([
      apiService.get('/admin/jobs'),
      apiService.get('/admin/jobs/status')
    ])
    jobs.value = jobsResponse.data
    schedulerStatus.value = statusResponse.data
  } catch (error) {
    console.error('Error loading jobs:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить список задач',
      life: 5000
    })
  } finally {
    loadingJobs.value = false
  }
}

const runJobNow = async (jobId) => {
  actionLoading.value = jobId + '_run'
  try {
    await apiService.post(`/admin/jobs/${jobId}/run`)
    toast.add({
      severity: 'success',
      summary: 'Задача запущена',
      detail: `${getJobDisplayName(jobId)} запланирована на выполнение`,
      life: 3000
    })
    await loadJobs()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось запустить задачу',
      life: 5000
    })
  } finally {
    actionLoading.value = null
  }
}

const pauseJob = async (jobId) => {
  actionLoading.value = jobId + '_pause'
  try {
    await apiService.post(`/admin/jobs/${jobId}/pause`)
    toast.add({
      severity: 'info',
      summary: 'Задача приостановлена',
      detail: getJobDisplayName(jobId),
      life: 3000
    })
    await loadJobs()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось приостановить задачу',
      life: 5000
    })
  } finally {
    actionLoading.value = null
  }
}

const resumeJob = async (jobId) => {
  actionLoading.value = jobId + '_resume'
  try {
    await apiService.post(`/admin/jobs/${jobId}/resume`)
    toast.add({
      severity: 'success',
      summary: 'Задача возобновлена',
      detail: getJobDisplayName(jobId),
      life: 3000
    })
    await loadJobs()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось возобновить задачу',
      life: 5000
    })
  } finally {
    actionLoading.value = null
  }
}

const loadLogsStats = async () => {
  loadingStats.value = true
  try {
    const response = await apiService.get('/admin/jobs/sync-logs/stats')
    logsStats.value = response.data
  } catch (error) {
    console.error('Error loading logs stats:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить статистику логов',
      life: 5000
    })
  } finally {
    loadingStats.value = false
  }
}

const confirmCleanup = () => {
  cleanupDialogVisible.value = true
}

const executeCleanup = async () => {
  cleanupLoading.value = true
  try {
    const response = await apiService.post(`/admin/jobs/sync-logs/cleanup?retention_days=${cleanupDays.value}&dry_run=false`)
    const result = response.data
    
    cleanupDialogVisible.value = false
    
    toast.add({
      severity: 'success',
      summary: 'Очистка завершена',
      detail: `Удалено ${result.deleted.toLocaleString()} записей`,
      life: 5000
    })
    
    await loadLogsStats()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось очистить логи',
      life: 5000
    })
  } finally {
    cleanupLoading.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadJobs()
  loadLogsStats()
})
</script>

<style scoped>
.admin-page {
  min-height: calc(100vh - 60px);
  background: var(--surface-ground);
}

.stat-card {
  border: 1px solid var(--surface-200);
}

.jobs-table :deep(.p-datatable-header) {
  background: transparent;
  border: none;
}

.jobs-table :deep(.p-datatable-tbody > tr) {
  transition: background-color 0.2s;
}

.jobs-table :deep(.p-datatable-tbody > tr:hover) {
  background: var(--surface-50) !important;
}
</style>
